import { useCallback, useId, useState, type ReactNode } from 'react';

import { useAuth } from '../auth/AuthContext';

import {
  attributsRabat,
  cleDeStockage,
  ecrireEtatRabat,
  etatAffiche,
  etatInitial,
  idsRabat,
  ligneDeTete,
  lireEtatRabat,
} from './section-rabattable';

/**
 * La section rabattable de la console — MV-UX-01, décision F-11 du fondateur :
 * « certaines sections du dashboard principal tuteur, ou dashboard secondaire,
 * et certaines tuiles étaient rabattables, pour alléger les contenus de page ».
 *
 * UNE SEULE PIÈCE. Tout écran du chantier qui replie une section ou une tuile
 * passe par elle (acceptation de MV-UX-01). Elle reprend l'interface de la
 * `Section` de la maquette (`titre`, `sous`, `action`) et y ajoute le rabat.
 *
 * ─── Un bouton et une région, pas `<details>` ───────────────────────────────
 *
 * La feuille porte déjà un `<details>` accessible (`.trend__table`). Il
 * convient à ce qu'il fait là-bas — un lien « voir en tableau » sans titre ni
 * état à retenir. Il ne convient pas ici, pour quatre raisons :
 *
 * 1. le titre d'une section est un TITRE (`h2`–`h4`), que la liste des titres
 *    d'un lecteur d'écran doit rendre. Dans un `<summary>`, le rôle de titre
 *    est perdu par plusieurs lecteurs d'écran ;
 * 2. l'action d'en-tête est un bouton à côté du titre. Dans un `<summary>`,
 *    c'est un contrôle imbriqué dans un contrôle : un clic y bascule aussi la
 *    section, sauf à le rattraper à la main ;
 * 3. `aria-expanded` et `aria-controls` sont demandés. `<summary>` ne les
 *    porte pas : les y poser double l'annonce native ;
 * 4. le mode contrôlé et la mémoire supposent que React possède l'état. Avec
 *    `<details>`, le navigateur bascule `open` de son côté avant React.
 *
 * Ce qui est repris du motif : le filet de focus, et l'essentiel — Entrée et
 * Espace viennent du `<button>` natif, sans un seul gestionnaire de touche.
 *
 * ─── Ce que le composant garantit ───────────────────────────────────────────
 *
 * - Le contenu replié reste MONTÉ et porte `hidden` : il sort de l'ordre de
 *   tabulation et de l'arbre d'accessibilité, et une saisie en cours dans une
 *   section survit à son repli.
 * - Le résumé n'apparaît que repliée. C'est lui qui allège sans cacher :
 *   « 3 acquises · 2 à consolider ».
 * - L'action d'en-tête est hors du bouton : elle ne bascule jamais la section.
 */
export interface SectionRabattableProps {
  /** Le titre. Il nomme la section ; c'est aussi le nom du bouton. */
  readonly titre: string;
  /**
   * Une ligne, visible seulement quand la section est repliée. Du texte ou des
   * pastilles — jamais un lien ni un bouton : elle est dans le bouton d'en-tête.
   */
  readonly resume?: ReactNode;
  /**
   * Le sous-titre de la `Section` de la maquette (une date, une légende),
   * visible seulement quand la section est ouverte. Même contrainte que
   * `resume` : rien d'interactif.
   */
  readonly sous?: ReactNode;
  /** Actions d'en-tête, à droite. Elles ne basculent pas la section. Prévues pour `.btn--sm`. */
  readonly actions?: ReactNode;
  /** `section` : un bloc de page. `tuile` : une carte compacte de tableau de bord. */
  readonly variante?: 'section' | 'tuile';
  /** L'état d'arrivée quand l'utilisateur n'a rien choisi. Défaut : ouverte. */
  readonly ouverteParDefaut?: boolean;
  /**
   * Retient le choix dans le navigateur, par utilisateur et par clé
   * (« portrait.competences »). Sans elle, rien n'est retenu.
   */
  readonly cleMemoire?: string;
  /** Mode contrôlé : le parent possède l'état. `ouverteParDefaut` et `cleMemoire` sont alors ignorées. */
  readonly ouverte?: boolean;
  /** Appelé à chaque bascule, avec l'état demandé. */
  readonly surBascule?: (ouverte: boolean) => void;
  /** Niveau du titre. Défaut : 3 pour une section, 4 pour une tuile. */
  readonly niveauTitre?: 2 | 3 | 4;
  readonly children: ReactNode;
}

export function SectionRabattable({
  titre,
  resume,
  sous,
  actions,
  variante = 'section',
  ouverteParDefaut = true,
  cleMemoire,
  ouverte: ouverteControlee,
  surBascule,
  niveauTitre,
  children,
}: SectionRabattableProps) {
  const { user } = useAuth();
  const cle = cleDeStockage(user?.id, cleMemoire);
  const ids = idsRabat(useId());

  // L'état initial vient de la mémoire, pas du défaut : sans ça, une section
  // repliée la veille s'ouvrirait un cadre avant de se refermer.
  const [interne, setInterne] = useState(() => ({
    cle,
    ouverte: etatInitial(ouverteParDefaut, lireEtatRabat(cle)),
  }));
  // La clé a changé sous un composant resté monté (autre utilisateur, autre
  // élève) : on relit la mémoire de la nouvelle clé, pendant le rendu.
  let courant = interne;
  if (interne.cle !== cle) {
    courant = { cle, ouverte: etatInitial(ouverteParDefaut, lireEtatRabat(cle)) };
    setInterne(courant);
  }

  const controlee = ouverteControlee !== undefined;
  const ouverte = etatAffiche(ouverteControlee, courant.ouverte);

  const basculer = useCallback(() => {
    const suivante = !ouverte;
    if (!controlee) {
      setInterne({ cle, ouverte: suivante });
      ecrireEtatRabat(cle, suivante);
    }
    surBascule?.(suivante);
  }, [ouverte, controlee, cle, surBascule]);

  const ligne = ligneDeTete(ouverte, { resume, sous });
  const a11y = attributsRabat(ids, { ouverte, ligneAffichee: ligne !== null, variante });
  const Titre = `h${niveauTitre ?? (variante === 'section' ? 3 : 4)}` as 'h2' | 'h3' | 'h4';

  return (
    <section className={`card rabat rabat--${variante}`}>
      <header className="rabat__tete">
        <Titre className="rabat__titre">
          <button type="button" className="rabat__bouton" onClick={basculer} {...a11y.bouton}>
            <svg
              className="rabat__chevron"
              viewBox="0 0 16 16"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M6 3.5 10.5 8 6 12.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="rabat__textes">
              <span className="rabat__libelle" id={ids.libelle}>
                {titre}
              </span>
              {ligne && (
                <span className={`rabat__ligne rabat__ligne--${ligne.nature}`} id={ids.ligne}>
                  {ligne.contenu}
                </span>
              )}
            </span>
          </button>
        </Titre>
        {actions !== undefined && actions !== null && (
          <div className="rabat__actions">{actions}</div>
        )}
      </header>
      <div className="rabat__corps" {...a11y.corps}>
        {children}
      </div>
    </section>
  );
}
